import {
  DeliveryMethods,
  PaymentMethods,
  HistoryLogs,
  SelfDeliveryMethods,
  TcDeliveryTypes,
  ReceivingMethods,
  GettingFile,
} from '@/enums';
import { Cart } from '@/modules/cart/types';
import {
  Filters, TableFilterItem, SimpleEnumType, ApiPagination, BaseResponse,
} from '@/types';
import { OrderStatuses } from '@/enums/statuses/orders';
import { PaymentsStatuses } from '@/enums/statuses/payments/';
import { ShipmentStatuses } from '@/enums/statuses/shipment';
import { Currencies } from '@/enums/prices/';
import { TransportCompany } from '@/modules/organizations/types';
import { AlfaApiLoanApplications } from '@/modules/alfa-api-loan-applications/types';

export namespace Orders {
  export enum Pages {
    ORDERS = 'orders',
    ORDER_DETAIL = 'order',
    ORDERS_INVOICES = 'invoices',
  }

  export type RequestFilters = {
    [key: string]: unknown,
    search: string,
    order_statuses: number[],
    payment_type: number | null,
    dates: string[],
    shipment_dates: string[],
    organization: number | null,
    external_organization: string | null,
    contracts: number[],
    users: number[],
    shipment_statuses: number[],
    is_unread_only: boolean,
    categories: number[],
  }

  export interface Item {
    [key: string]: unknown,
    id: number,
    external_number: string,
    cart_number: string | null,
    date: string | null,
    shipment_date: string | null,
    order_status: SimpleEnumType<OrderStatuses> | null,
    payment_status: SimpleEnumType<PaymentsStatuses> | null,
    payment_type: SimpleEnumType<PaymentMethods> | null,
    shipment_status: SimpleEnumType<ShipmentStatuses> | null,
    total_price: number,
    organization: string,
    payment_date: string | null,
    user: string,
    project: string | null,
    warehouse: string | null,
    delivery_type: string | null,
    comment: string | null,
    is_unread: boolean,
    updated_at: string | null,
    categories: string | null,
    belongs_to_set: boolean,
    has_files: boolean,
  }

  export interface TableFilters {
    organizations: TableFilterItem[],
    external_organizations: string[],
    users: TableFilterItem[],
    categories: TableFilterItem[],
    contracts: TableFilterItem[],
    statuses: TableFilterItem[],
    shipment_statuses: TableFilterItem[],
    payment_types: TableFilterItem[],
  }

  export interface Additional {
    total_price: number,
  }

  export interface Response extends ApiPagination {
    data: {
      items: Item[],
      filters: TableFilters,
      additional: Additional
    }
  }

  export interface Order {
    [key: string]: unknown;
    id?: number;
    project_id: number | null;
    organization: number | null;
    warehouse: number | null;
    payment_type: PaymentMethods | null;
    delivery_type: DeliveryMethods | null;
    products: Cart.CartItem[];
    delivery_address: number | null;
    transport_company_id: number | null;
    self_delivery_type?: SelfDeliveryMethods | null,
    tc_delivery_type?: TcDeliveryTypes | null,
    tc_receiving_method?: ReceivingMethods | null,
    is_express?: boolean | null,
    is_dellin?: boolean | null,
    delline_place?: string | null,
    delline_place_id?: number | null,
    delline_terminal?: string | null,
    delline_terminal_id?: number | null,
    comment?: string | null,
    external_number?: number | null,
    has_unread_change: boolean,
    balance_credit_limit: string | null,
    is_realization_contract: boolean,
    has_realization_contract: boolean,
    is_cash_registers_auto_addition: boolean,
  }

  export interface OrderOrganization {
    id: number;
    name_organization: string | null;
    is_credit_enabled: boolean;
    not_final_alfa_api_loan_applications_exists?: boolean | null;
  }

  export interface OrderProductItem {
    id: number;
    nomenclature: string | null;
    product_code: string | number | null;
  }

  export interface OrderProductItemPrices {
    price_rub: number | null;
    total_price_rub: number | null;
    price_nds_rub: number | null;
    nds: number | null;
  }

  export interface OrderProductItemDelivery {
    [key: string]: unknown;
    delivery_type: SimpleEnumType<DeliveryMethods | null>;
    warehouse: {
      id: number | null;
      name: string | null;
      address: string | null;
    };
    delivery_address: {
      id?: number;
      address: string;
    };
    transport_company: TransportCompany | null;
  }

  export interface OrderProductInfo {
    [key: string]: unknown;
    id: number;
    payment_type: SimpleEnumType<PaymentMethods>;
    product: OrderProductItem;
    prices: OrderProductItemPrices;
    delivery: OrderProductItemDelivery;
    quantity: number;
    reserve_quantity: number | null;
    shipment_quantity: number | null;
    remnant_quantity: number | null;
    volume: string | null;
    weight: number | null;
    shipment_date: string | null;
    canceled_at: string | null;
    shipment_status?: {
      id: number,
      name: string,
      slug: string
    };
    related_set: string | null;
  }

  export interface AlfaApiClient {
    id: number;
    atol_organization_id: number;
    order_min_sum: number;
    created_at: string;
    updated_at: string;
  }

  export interface AtolOrganization {
    id: number;
    name: string;
    external_id: string;
    recipient_name: string;
    recipient_inn: string;
    recipient_kpp: string;
    created_at: string;
    updated_at: string;
    alfa_api_client: AlfaApiClient | null;
  }

  export interface OrderDetailedInfo {
    [key: string]: unknown;
    id: number;
    date: string | null;
    shipment_date: string | null;
    products: OrderProductInfo[];
    order_status: SimpleEnumType<OrderStatuses>;
    order_payment_status: SimpleEnumType<PaymentsStatuses>;
    order_shipment_status: SimpleEnumType<ShipmentStatuses>;
    cart_payment_type: SimpleEnumType<PaymentMethods>;
    organization: OrderOrganization | null;
    atol_organization: AtolOrganization | null;
    total_price: number | null;
    total_volume: number | null;
    total_weight: number | null;
    project_id: string | null;
    contract: string | null;
    is_allowed_cancel: boolean;
    is_limit_exceeded: boolean;
    comment: string | null;
    register_realization_id: number | null;
    transport_company: string | null;
    delivery_address: string | null;
    delivery_type: string | null;
    alfa_api_loan_applications?: AlfaApiLoanApplications.AlfaApiLoanApplication[];
  }

  export interface OrderCancelReasons {
    [key: string]: unknown;
    id: number | null;
    name: string | null
  }

  export interface OrderUpdated {
    id?: number;
  }

  export interface OrderDeliveryUpdated extends OrderUpdated {
    [key: string]: unknown;
    delivery_type: DeliveryMethods | null;
    warehouse_id: number | null;
    transport_company_id: number | null;
    delivery_address_id: number | null;
    comment: string | null;
  }

  export interface OrderQuantityUpdated extends OrderUpdated {
    quantity: number | null;
  }

  export interface OrderShipmentDateUpdated extends OrderUpdated {
    shipment_date: string | null;
  }

  export type UpdatedOrderDetails =
    | OrderDeliveryUpdated
    | OrderQuantityUpdated
    | OrderShipmentDateUpdated;

  export interface OrderDocument {
    [key: string]: unknown;
    id: number;
    date: string | null;
    name: string | null;
    slug: GettingFile.Slugs;
  }

  export interface OrderHistoryLog {
    [key: string]: unknown;
    id: number;
    date: string | null;
    description: SimpleEnumType<HistoryLogs>;
    author: {
      id: number | null;
      full_name: string | null;
    };
  }

  export interface DeliveryType {
    id: number;
    name: string;
    slug: string;
  }
  export interface SelfDeliveryType {
    id: number;
    name: string;
    slug: string | null;
  }

  export interface PaymentType {
    id: number;
    name: string;
    slug: string;
  }

  export interface TcReceivingMethod {
    id: number;
    name: string;
    slug: string;
  }
  export interface LastCardType {
    id: number | null;
    organization_id: number | null;
    payment_type: PaymentType | null;
    warehouse_id: number | null;
    delivery_type: DeliveryType | null;
    self_delivery_type: SelfDeliveryType | null;
    delivery_address_id: number | null;
    transport_company_id: number | null;
    tc_delivery_type: string | null;
    is_express: boolean | null;
    tc_receiving_method: TcReceivingMethod | null;
  }

  export interface ProductShipment {
    [key: string]: unknown;
    order_product_id: number;
    product_id: number;
    nomenclature: string;
    product_code: string;
    reserve_quantity: number;
    external_index: number;
    order_id: number;
  }

  export interface OrderShipment {
    order_id: number;
    order_external_number: string;
    delivery_type: string;
    delivery_address: string;
    range_delivery: null;
    delivery_price: number;
    shipment_price: number;
    inactive_shipment_dates: string[];
    products: ProductShipment[];
  }

  export interface OrderItemWithoutDate {
    [key: string]: unknown;
    id: number;
    order_id: number,
    external_index: number,
  }

  export interface OrderItem {
    [key: string]: unknown;
    id: number;
    order_id: number,
    external_index: number,
    shipment_date: string,
  }

  export interface ShipmentOrdersDate {
    items: OrderItem[];
  }

  export interface ChangedShipmentDate {
    date: string,
    order: ProductShipment[],
  }

  export interface OrderProductIds {
    products: {
      order_id: number,
      external_index: number
    }[];
  }

  export interface CreateOrderResponse {
    cart_id: number | null;
    external_cart_number?: string | null;
    price: number;
    total_order_price: number;
    has_zip_products: boolean;
    no_order_products_count: number;
    no_order_products: {
      product_code: number;
      nomenclature: string;
      quantity: number;
    }[];
    message: string | null;
  }

  export type UpdateOrderDTO = {
    key: keyof Cart.CartItem;
    value: unknown;
    id: number;
  };

  export interface OrdersInvoiceInfo {
    [key: string]: unknown;
    id: number;
    order_id: number | null;
    organization: string | null;
    total_price: number | null;
    currency: SimpleEnumType<Currencies>;
    name: string | null;
    created_at: string | null;
    payment_at: string | null;
    file_extension: string | null;
    user_full_name: string | null;
    has_unread_change: boolean;
    order_payment_status_name?: string;
  }

  export interface UsersFilter {
    id: string | number,
    name: string;
  }

  export interface OrdersInvoicesFilters extends Filters {
    [key: string]: unknown;
    date: string | null;
    organization: string | null;
    user_ids: UsersFilter[];
    only_has_unread_changes: boolean;
  }

  export interface CancelResonDialog {
    [key: string]: unknown,
    visible: boolean,
    cancel_reason_id: number,
  }

  export interface OrdersInvoicesList extends BaseResponse, ApiPagination {
    data: {
      items: OrdersInvoiceInfo[];
      dates: string[];
      organizations: string[];
      users: UsersFilter[];
    };
  }

  export interface CancelReasonsList extends BaseResponse, ApiPagination {
    data: {
      items: Orders.OrderCancelReasons[];
    };

  }

  export interface ShipmentInformation extends BaseResponse {
    items: Orders.OrderShipment[];
  }
}
